import { Typography, Button } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { BookingSettingsCounter } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsCounter';
import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

interface Props {
  adults: string | undefined;
  pChildren: string | undefined;
  infants: string | undefined;
  maxPartySize: number | undefined;
  isLoading?: boolean;
  isOpen: boolean;
  hasHeaderCancelButton: boolean;
  close: () => void;
}

export const BookingSettingsGuestDialog = ({ maxPartySize, adults, pChildren, infants, isOpen, close, isLoading = false, hasHeaderCancelButton = false }: Props) => {
  const router = useRouter();
  const [a, setA] = useState(adults ? Number(adults) : 1);
  const [c, setC] = useState(pChildren ? Number(pChildren) : 0);
  const [i, setI] = useState(infants ? Number(infants) : 0);

  const maxGuests = useMemo(() => {
    if (!maxPartySize) return 0;
    return maxPartySize;
  }, [maxPartySize]);

  const maxChildren = useMemo(() => {
    if (!pChildren && !maxPartySize) return 1;
    const diff = maxGuests - Number(a) - Number(c);
    const maxChildren = Number(c) + diff;
    return maxChildren;
  }, [pChildren, maxPartySize, maxGuests, a, c]);

  const maxAdults = useMemo(() => {
    if (!adults && !maxPartySize) return 1;
    const diff = maxGuests - Number(a) - Number(c);
    const maxAdults = Number(a) + diff;
    return maxAdults;
  }, [adults, maxPartySize, maxGuests, a, c]);

  const onSubmit = () => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [CheckoutParamEnum.adults]: a,
          [CheckoutParamEnum.children]: c,
          [CheckoutParamEnum.infants]: i,
        },
      },
      undefined,
      { shallow: true, scroll: false },
    );
    close();
  };

  return (
    <ConfirmationDialog
      dialogTitle="Guests"
      isDialogOpen={isOpen}
      handleCloseDialog={close}
      hasBottomBar={false}
      hasHeaderCancelButton={hasHeaderCancelButton}
      maxWidth="xs"
    >
      {isLoading ? <Typography>We are confirming the dates you have selected, please wait a moment while we process your request.<br /><br /><strong>If you have not set the dates...</strong><br />Please close this window and try again to continue.</Typography> :
        <Typography>
          The maximum number of adults and children cannot exceed <strong>{maxGuests} people</strong>. Infants are
          excluded from the group size total.
        </Typography>
      }
      <AppDivider marginVertical="24px" />
      <LoadingSkeleton isLoading={isLoading}>
        <BookingSettingsCounter
          title="Adults"
          subtitle=""
          maximumValue={maxAdults}
          value={a}
          setValue={setA}
          minimumValue={1}
        />
        <BookingSettingsCounter
          title="Children"
          subtitle="Ages 2-12"
          maximumValue={maxChildren}
          value={c}
          setValue={setC}
        />
        <BookingSettingsCounter title="Infants" subtitle="Under 2" value={i} setValue={setI} />
        <Button variant="contained" color="secondary" fullWidth size="large" onClick={onSubmit}>
          Continue
        </Button>
      </LoadingSkeleton>
    </ConfirmationDialog>
  );
};
